// components/payments/PaymentPanel.tsx
import React from "react";
import Payment from "assets/images/brands/Payment.png"

type Variant = "signage" | "realtor" | "agent" | "teamMember" | "other";
type DynMethod = { _id: string; name: string };

/** Minimal surface from Formik we actually use (structurally compatible). */
type FormikLike = {
  values: {
    enable_partial_payment?: boolean;
    partial_amount?: number;
    transactionId?: string;
    [k: string]: any;
  };
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => any;
};

type Props = {
  variant: Variant;                          // "signage" => all methods + partial
  remainingAmount: number;
  orderStatus: number;
  paymentStatus: string;

  selectedPaymentMethod: { name: string; _id: string } | null;
  setSelectedPaymentMethod: (v: { name: string; _id: string }) => void;
  orderType?:string;

  /** Optional (but recommended for signage/partial) */
  formik?: FormikLike;
  maxAllowed: number;

  dynamicMethods?: DynMethod[];

  /** OPTIONAL: parent handler for offline/dynamic */
  onSubmitOffline?: (method: { name: string; _id: string }) => void;

  /** Your Stripe node (e.g., <Elements>...</Elements>) */
  onlinePaymentNode?: React.ReactNode;

  /** OPTIONAL: lets you inject your own PaymentMethods block */
  paymentMethodsNode?: React.ReactNode;

  /** Payment history to check if dynamic methods were already used */
  paymentInfo?: Array<{
    method: string;
    payment_status?: string;
    [key: string]: any;
  }>;

  /** Checkout modal: hide duplicate header, method label, and online radio row */
  compactLayout?: boolean;
};

type PaymentInfoRow = NonNullable<Props["paymentInfo"]>[number];

const labelFor = (k: string) => {
  const map: Record<string, string> = {
    cheque: "Cheque",
    bank_transfer: "Bank Transfer",
    store_paid: "Store Paid",
    net_30: "Net 30",
    online: "Credit / Debit Card",
  };
  return map[k] || k;
};

/** Store Paid: full payment only, no amount field — show only Transaction ID */
const isStorePaid = (k: string) => norm(k) === "store_paid";
const norm = (v: string) => String(v || "").toLowerCase().replace(/\s+/g, "_");
const isOffline = (k: string) => norm(k) !== "online";

const PaymentPanel: React.FC<Props> = ({
  variant,
  remainingAmount,
  orderStatus,
  paymentStatus,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  formik,
  maxAllowed,
  dynamicMethods = [] as DynMethod[],
  onSubmitOffline,
  onlinePaymentNode,
  paymentMethodsNode,
  paymentInfo = [] as PaymentInfoRow[],
  orderType,
  compactLayout = false,
}) => {
  // console.log("PaymentPanel rendered with orderType:", orderType, "orderStatus:", orderStatus, "paymentStatus:", paymentStatus, "remainingAmount:", remainingAmount, "selectedPaymentMethod:", selectedPaymentMethod);
  // ✅ Move all hooks to the top BEFORE any conditional returns
  const isPaid = paymentStatus === "Paid" || Number(remainingAmount) === 0;
  const isSignage = variant === "signage";

  // Static methods; keep ONLINE last. Store Paid after bank_transfer (Signage only).
  const staticMethods: string[] = isSignage
    ? ["cheque", "bank_transfer", "store_paid", "online"]
    : ["online"];

  // Local state (sync with Formik if provided)
  const [localEnablePartial, setLocalEnablePartial] = React.useState<boolean>(
    Boolean(formik?.values?.enable_partial_payment)
  );
  const [localPartialAmount, setLocalPartialAmount] = React.useState<number>(
    Number(formik?.values?.partial_amount ?? 0)
  );
  const [localTxnId, setLocalTxnId] = React.useState<string>(
    String(formik?.values?.transactionId ?? "")
  );
  const [partialCreditMessage, setPartialCreditMessage] = React.useState<string>("");

  React.useEffect(() => {
    if (!formik) return;
    setLocalEnablePartial(Boolean(formik.values?.enable_partial_payment));
    setLocalPartialAmount(Number(formik.values?.partial_amount ?? 0));
    setLocalTxnId(String(formik.values?.transactionId ?? ""));
  }, [formik?.values?.enable_partial_payment, formik?.values?.partial_amount, formik?.values?.transactionId]);

  React.useEffect(() => {
    if (!partialCreditMessage) return;
    const timeoutId = window.setTimeout(() => {
      setPartialCreditMessage("");
    }, 5000);
    return () => window.clearTimeout(timeoutId);
  }, [partialCreditMessage]);

  // Derived state for dynamic-method reset effect — must run before any early return (Rules of Hooks).
  const selectedKey = norm(String(selectedPaymentMethod?.name || ""));
  const isDynamicSelected =
    selectedKey !== "" &&
    selectedKey !== "online" &&
    selectedKey !== "cheque" &&
    selectedKey !== "bank_transfer" &&
    selectedKey !== "store_paid";
  const visibleStaticMethods = localEnablePartial
    ? staticMethods.filter((m) => norm(m) !== "store_paid")
    : staticMethods;
  const isPartialPaymentStatus =
    norm(String(paymentStatus || "")) === "partial_payment";
  const shouldHideDynamicMethods = localEnablePartial || isPartialPaymentStatus;
  const shouldShowDynamicMethods = !shouldHideDynamicMethods;

  React.useEffect(() => {
    // If current selection gets hidden by partial mode/status, clear selection (no fallback).
    if (!shouldHideDynamicMethods) return;
    if (!isDynamicSelected) return;
    setSelectedPaymentMethod({ name: "", _id: "" });
  }, [shouldHideDynamicMethods, isDynamicSelected, visibleStaticMethods, setSelectedPaymentMethod]);

  // ✅ Conditional returns AFTER all hooks
  // Hide if cancelled (700) or void (800) for removal orders
  if (orderType === "removalOrder" && (orderStatus === 700 || orderStatus === 800)) return null;
  // Hide if cancelled (700) or void (800) for installation orders
  if (orderType === "installationOrder" && (orderStatus === 600)) return null;

  // Hide if cancelled for other order types
  if (orderStatus === 400 && orderType !== "installationOrder" && orderType !== "removalOrder") return null;

  const togglePartial = (force?: boolean) => {
    const next = typeof force === "boolean" ? force : !localEnablePartial;
    setLocalEnablePartial(next);
    formik?.setFieldValue?.("enable_partial_payment", next);
    if (next && isDynamicSelected) {
      setSelectedPaymentMethod({ name: "", _id: "" });
      setPartialCreditMessage("Partial payments are not available for credit transactions");
    }
    if (!next) {
      setLocalPartialAmount(0);
      formik?.setFieldValue?.("partial_amount", 0);
    }
  };
  const handlePartialAmount = (value: string) => {
    // Allow empty string, numbers, and decimal points while typing
    const numValue = parseFloat(value) || 0;
    const clamped = Math.max(0, Math.min(numValue, maxAllowed));
    setLocalPartialAmount(clamped);
    formik?.setFieldValue?.("partial_amount", clamped);
  };
  const handleTxn = (val: string) => {
    setLocalTxnId(val);
    formik?.setFieldValue?.("transactionId", val);
  };
  const normalizePartialBeforePayment = () => {
    if (!localEnablePartial) return;
    const enteredAmount = Number(localPartialAmount || 0);
    if (enteredAmount > 0) return;
    setLocalEnablePartial(false);
    setLocalPartialAmount(0);
    formik?.setFieldValue?.("enable_partial_payment", false);
    formik?.setFieldValue?.("partial_amount", 0);
  };

  const payWithLabel =
    selectedPaymentMethod && isOffline(selectedPaymentMethod.name)
      ? `Pay With ${labelFor(norm(selectedPaymentMethod.name))}`
      : "Pay";

  const remainingAfterPartial = Math.max(
    0,
    Number(remainingAmount) - Number(localEnablePartial ? localPartialAmount : 0)
  );

  // Check if any dynamic method has been used in payment history
  const hasUsedDynamicMethod = paymentInfo.some(payment => {
    const method = norm(payment.method);
    return method !== "online" && method !== "cheque" && method !== "bank_transfer" && method !== "store_paid";
  });

  // Sort dynamic methods to keep any "online" style last.
  // In Signage, hide dynamic "store_paid" so we only show the static Store Paid (below Bank Transfer).
  const dynFiltered = isSignage
    ? dynamicMethods.filter((m) => norm(m.name) !== "store_paid")
    : dynamicMethods;
  const dynSorted = [...dynFiltered].sort((a, b) => {
    const ao = norm(a.name) === "online" ? 1 : 0;
    const bo = norm(b.name) === "online" ? 1 : 0;
    return ao - bo; // online to the end
  });

  const dynamicMethodsBlock =
    shouldShowDynamicMethods && dynSorted.length > 0 && !paymentMethodsNode && !hasUsedDynamicMethod ? (
      <div className="mt-2">
        {dynSorted.map((m) => {
          const mk = norm(m.name);
          const isSelected = selectedKey === mk;
          const offline = isOffline(mk);
          return (
            <div key={m._id} className="mb-2">
              <div
                className={`method-row ${isSelected ? "active" : ""}${compactLayout ? " compact-method-bar" : ""}`}
                onClick={() => {
                  normalizePartialBeforePayment();
                  setSelectedPaymentMethod({ name: m.name, _id: m._id });
                }}
              >
                <div className="form-check m-0">
                  <input
                    className="form-check-input "
                    type="radio"
                    name={compactLayout ? "paymentMethod" : "dynamicMethod"}
                    id={`pm_dyn_${m._id}`}
                    checked={isSelected}
                    onChange={() => {
                      normalizePartialBeforePayment();
                      setSelectedPaymentMethod({ name: m.name, _id: m._id });
                    }}
                  />
                  <label htmlFor={`pm_dyn_${m._id}`} className="form-check-label">
                    {m.name}
                  </label>
                </div>
              </div>

              {isSelected && offline && (
                <button
                  type="button"
                  className="btn btn-primary table-addbutton br-8"
                  onClick={() => {
                    normalizePartialBeforePayment();
                    onSubmitOffline?.({ name: m.name, _id: m._id });
                  }}
                  disabled={!onSubmitOffline}
                  title={!onSubmitOffline ? "Handler not provided" : undefined}
                >
                  {`Pay With ${m.name}`}
                </button>
              )}

              {isSelected && !offline && onlinePaymentNode && (
                <div className={compactLayout ? "compact-online-node" : "ms-4 mt-2"}>{onlinePaymentNode}</div>
              )}
            </div>
          );
        })}
      </div>
    ) : null;

  return (
    <div className="card shadow-lg shadow-border-2">
      {!compactLayout && (
        <div className="px-3 py-3 rounded-top d-flex justify-content-between align-items-center" style={{ backgroundColor: "#eff6ff" }}>
          <h6 className="mb-0 d-flex align-items-center">
            <span
              aria-hidden="true"
              className="image-icons"
              style={{
                backgroundColor: "#00A63E",
                WebkitMaskImage: `url(${Payment})`,
                maskImage: `url(${Payment})`,
              }}
            />
            <p className="mb-0 ms-2 order-tittle">Payment</p>
          </h6>
          {!isPaid && <div className="text-danger fw-bold fs-6">${Number(remainingAmount).toFixed(2)}</div>}
        </div>
      )}

      <div className="card-body">
        {isPaid ? (
          <p className="text-success fw-semiboldorder-card-title  text-center mb-0">Your payment is completed / approved</p>
        ) : (
          <>
            {partialCreditMessage ? (
              <div className="text-center mb-2">
                <span className="text-danger fw-semibold">{partialCreditMessage}</span>
              </div>
            ) : null}
            <div className="order-card-title small mb-2">Select Payment Method</div>

            {/* Enable Partial Payment — RADIO look; details block immediately below */}
            {isSignage && (
              <div className="mb-2">
                <div
                  className={`method-row ${localEnablePartial ? "active" : ""}`}
                  onClick={() => togglePartial()}
                >
                  <div className="form-check m-0">
                    <input
                      type="radio"
                      name="pm_partial_group"
                      className="form-check-input"
                      id="pm_partial"
                      checked={localEnablePartial}
                      onChange={() => togglePartial(true)}
                    />
                    <label htmlFor="pm_partial" className="form-check-label ">
                      Enable Partial Payment
                    </label>
                  </div>
                </div>

                {localEnablePartial && (
                  <div className="ms-4 mt-2 partial-box p-3 border rounded-3">
                    <div className="mb-2 fw-semibold order-card-title">Amount to Collect Now :</div>
                    <div className="d-flex align-items-end gap-2" style={{ maxWidth: 340 }}>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control br-8"
                        value={localPartialAmount || ""}
                        onChange={(e) => handlePartialAmount(e.target.value)}
                        min={0}
                        placeholder="0"
                        max={maxAllowed}
                      />
                      {/* no submit button here; collection is done via the chosen method below */}
                    </div>
                    <div className="mt-2 text-muted small">
                      Remaining Balance: ${remainingAfterPartial.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Static methods — Credit/Debit Card first, then details under selection */}
            {visibleStaticMethods.map((rawKey) => {
              const k = norm(rawKey);
              const isSelected = selectedKey === k;
              const offline = isOffline(k);

              return (
                <div key={k} className="mb-2">
                  <div
                    className={`method-row ${isSelected ? "active" : ""}${compactLayout ? " compact-method-bar" : ""}`}
                    onClick={() => {
                      normalizePartialBeforePayment();
                      setSelectedPaymentMethod({ name: rawKey, _id: rawKey });
                    }}
                  >
                    <div className="form-check m-0">
                      <input
                        className="form-check-input br-8"
                        type="radio"
                        name="paymentMethod"
                        id={`pm_${k}`}
                        checked={isSelected}
                        onChange={() => {
                          normalizePartialBeforePayment();
                          setSelectedPaymentMethod({ name: rawKey, _id: rawKey });
                        }}
                      />
                      <label htmlFor={`pm_${k}`} className="form-check-label">
                        {labelFor(k)}
                      </label>
                    </div>
                  </div>

                  {/* Details under the selected method */}
                  {isSelected && offline && (
                    <div className="ms-4 mt-2">
                      <label className="small fw-semibold mb-1 d-block">Transaction ID</label>
                      <input
                        type="text"
                        className="form-control soft-input br-8"
                        placeholder="Enter transaction ID"
                        value={localTxnId}
                        onChange={(e) => handleTxn(e.target.value)}
                      />

                      {/* Store Paid: only Transaction ID + Submit (no amount, full payment only) */}
                      {isStorePaid(k) ? (
                        <div className="mt-3">
                          <button
                            type="button"
                            className="btn btn-primary table-addbutton br-8"
                            onClick={() => onSubmitOffline?.({ name: rawKey, _id: rawKey })}
                            disabled={!onSubmitOffline}
                            title={!onSubmitOffline ? "Handler not provided" : undefined}
                          >
                            Submit Payment
                          </button>
                        </div>
                      ) : (
                        /* Cheque / Bank Transfer: Amount + Submit */
                        <div className="mt-3">
                          <div className="row g-2 align-items-end">
                            <div className="col-12 col-md-5" id="row-overlap">
                              <label className="small fw-semibold mb-1 d-block">Amount</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control br-8"
                                value={localEnablePartial ? (localPartialAmount || "") : Number(remainingAmount || 0).toFixed(2)}
                                onChange={(e) => localEnablePartial && handlePartialAmount(e.target.value)}
                                disabled={!localEnablePartial}
                                min={0}
                                max={maxAllowed}
                                placeholder="0"
                              />
                            </div>
                            <div className="col-12 col-md-7 d-grid" id="row-overlap">
                              <button
                                type="button"
                                className="btn btn-primary table-addbutton br-8"
                                onClick={() => {
                                  normalizePartialBeforePayment();
                                  onSubmitOffline?.({ name: rawKey, _id: rawKey });
                                }}
                                disabled={!onSubmitOffline}
                                title={!onSubmitOffline ? "Handler not provided" : undefined}
                              >
                                Submit Payment
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                  {/* Online (Stripe) directly under the selected card option */}
                  {isSelected && !offline && onlinePaymentNode && (
                    <div className={compactLayout ? "compact-online-node" : "ms-4 mt-2"}>{onlinePaymentNode}</div>
                  )}
                </div>
              );
            })}

            {dynamicMethodsBlock}

            {/* External PaymentMethods node from parent (Agent page), if used */}
            {paymentMethodsNode ? <div className="mt-3">{paymentMethodsNode}</div> : null}
          </>
        )}
      </div>

      {/* styles */}
      <style>{`
        .rounded-4 { border-radius: 16px; }
        .card-header { border-bottom: 1px solid #edf1f5; }
        .icon-chip {
          width: 28px; height: 28px; border-radius: 8px;
          display:inline-flex; align-items:center; justify-content:center;
          font-size:16px;
        }
        .chip-green { background:#EAF7F1; color:#10B981; }

        .method-row {
          border: 1px solid #e7e9ef;
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: box-shadow .15s ease, border-color .15s ease;
          background: #fff;
        }
        .method-row.active {
          border-color: #636568;
    box-shadow: 0 0 0 3px rgb(20 21 22 / 12%);
        }
    .form-check-input:checked {
    background-color: #c0cbdb ! important;
    border-color: #8395af ! important;
}
        .soft-input {
          background: #f3f6fb;
          border-color: #e9eef5;
        }
        .partial-box { background:#f9fbff; }
      `}</style>
    </div>
  );
};

export default PaymentPanel;













// // components/payments/PaymentPanel.tsx
// import React from "react";

// type Variant = "signage" | "realtor" | "agent" | "teamMember" | "other";
// type DynMethod = { _id: string; name: string };

// /** Minimal surface from Formik we actually use (structurally compatible). */
// type FormikLike = {
//   values: {
//     enable_partial_payment?: boolean;
//     partial_amount?: number;
//     transactionId?: string;
//     [k: string]: any;
//   };
//   setFieldValue: (field: string, value: any, shouldValidate?: boolean) => any;
//   handleChange: (e: React.ChangeEvent<any>) => void;
//   handleBlur: (e: React.FocusEvent<any>) => void;
// };

// type Props = {
//   variant: Variant;                          // "signage" => all methods + partial
//   remainingAmount: number;
//   orderStatus: number;
//   paymentStatus: string;

//   selectedPaymentMethod: string | null;
//   setSelectedPaymentMethod: (v: string) => void;

//   /** Optional (but recommended for signage/partial) */
//   formik?: FormikLike;
//   maxAllowed: number;

//   dynamicMethods?: DynMethod[];

//   /** OPTIONAL: parent handler for offline/dynamic */
//   onSubmitOffline?: (methodKey: string) => void;

//   /** Your Stripe node (e.g., <Elements>...</Elements>) */
//   onlinePaymentNode?: React.ReactNode;

//   /** OPTIONAL: lets you inject your own PaymentMethods block */
//   paymentMethodsNode?: React.ReactNode;
// };

// const PaymentPanel: React.FC<Props> = ({
//   variant,
//   remainingAmount,
//   orderStatus,
//   paymentStatus,
//   selectedPaymentMethod,
//   setSelectedPaymentMethod,
//   formik,
//   maxAllowed,
//   dynamicMethods = [],
//   onSubmitOffline,
//   onlinePaymentNode,
//   paymentMethodsNode
// }) => {
//   // Show nothing only if order is cancelled
//   if (orderStatus === 400) return null;

//   const isPaid = paymentStatus === "Paid" || Number(remainingAmount) === 0;
//   const isSignage = variant === "signage";

//   // visible static methods
//   const visibleStatic: Array<"cheque" | "bank_transfer" | "online"> = isSignage
//     ? ["cheque", "bank_transfer", "online"]
//     : ["online"];

//   const labelFor = (k: string) =>
//     ({ cheque: "Cheque", bank_transfer: "Bank Transfer", online: "Credit/Debit Card" } as any)[k] || k;

//   // ---- Local fallbacks so component works even if formik is not passed ----
//   const [localEnablePartial, setLocalEnablePartial] = React.useState<boolean>(
//     Boolean(formik?.values?.enable_partial_payment)
//   );
//   const [localPartialAmount, setLocalPartialAmount] = React.useState<number>(
//     Number(formik?.values?.partial_amount ?? 0)
//   );
//   const [localTxnId, setLocalTxnId] = React.useState<string>(String(formik?.values?.transactionId ?? ""));

//   // keep locals in sync if parent Formik updates
//   React.useEffect(() => {
//     if (!formik) return;
//     setLocalEnablePartial(Boolean(formik.values?.enable_partial_payment));
//     setLocalPartialAmount(Number(formik.values?.partial_amount ?? 0));
//     setLocalTxnId(String(formik.values?.transactionId ?? ""));
//   }, [formik?.values?.enable_partial_payment, formik?.values?.partial_amount, formik?.values?.transactionId]);

//   const enablePartial = isSignage && !isPaid;

//   const togglePartial = () => {
//     const next = !localEnablePartial;
//     setLocalEnablePartial(next);
//     formik?.setFieldValue?.("enable_partial_payment", next);
//     if (!next) {
//       setLocalPartialAmount(0);
//       formik?.setFieldValue?.("partial_amount", 0);
//     }
//   };

//   const handleAmount = (v: number) => {
//     const value = Number(isFinite(v as any) ? v : 0);
//     const clamped = value <= maxAllowed ? value : maxAllowed;
//     setLocalPartialAmount(clamped);
//     formik?.setFieldValue?.("partial_amount", clamped);
//   };

//   const handleTxn = (val: string) => {
//     setLocalTxnId(val);
//     formik?.setFieldValue?.("transactionId", val);
//   };

//   // Only show offline extras if a parent handler exists (avoid duplicate submit flows)
//   const showOfflineExtras = Boolean(
//     selectedPaymentMethod && selectedPaymentMethod !== "online" && onSubmitOffline
//   );

//   return (
//     <div className="card shadow-sm rounded-4 border-0 mt-4">
//       <div className="card-header bg-white rounded-top border-0 py-3 d-flex justify-content-between align-items-center">
//         <h6 className="mb-0 d-flex align-items-center">
//           <span className="icon-chip chip-green me-2">
//             <i className="bi bi-cash-coin" />
//           </span>
//           Payment
//         </h6>
//         {!isPaid && <div className="text-danger fw-bold fs-6">${Number(remainingAmount).toFixed(2)}</div>}
//       </div>

//       <div className="card-body">
//         {isPaid ? (
//           <p className="text-success fw-semibold text-center mb-0">Your payment is completed / approved</p>
//         ) : (
//           <>
//             <div className="text-muted small mb-2">Select Payment Method</div>

//             {/* Enable Partial Payment (signage only) */}
//             {enablePartial && (
//               <div className={`method-row ${localEnablePartial ? "active" : ""}`} onClick={togglePartial}>
//                 <div className="form-check m-0">
//                   <input
//                     type="checkbox"
//                     className="form-check-input"
//                     id="enable_partial_payment"
//                     checked={localEnablePartial}
//                     onChange={togglePartial}
//                   />
//                   <label htmlFor="enable_partial_payment" className="form-check-label">
//                     Enable Partial Payment
//                   </label>
//                 </div>
//               </div>
//             )}

//             {/* Static methods */}
//             {visibleStatic.map((key) => (
//               <div
//                 key={key}
//                 className={`method-row ${selectedPaymentMethod === key ? "active" : ""}`}
//                 onClick={() => setSelectedPaymentMethod(key)}
//               >
//                 <div className="form-check m-0">
//                   <input
//                     className="form-check-input"
//                     type="radio"
//                     name="paymentMethod"
//                     id={`pm_${key}`}
//                     checked={selectedPaymentMethod === key}
//                     onChange={() => setSelectedPaymentMethod(key)}
//                   />
//                   <label htmlFor={`pm_${key}`} className="form-check-label">
//                     {labelFor(key)}
//                   </label>
//                 </div>
//               </div>
//             ))}

//             {/* Extra fields for offline static/dynamic selections (only if parent handler provided) */}
//             {showOfflineExtras && (
//               <>
//                 <div className="mt-3">
//                   <div className="small fw-semibold mb-1">Transaction ID</div>
//                   <input
//                     type="text"
//                     className="form-control soft-input"
//                     placeholder="Enter transaction ID"
//                     value={localTxnId}
//                     onChange={(e) => handleTxn(e.target.value)}
//                   />
//                 </div>

//                 <div className="mt-3 d-flex align-items-end gap-2">
//                   <div style={{ width: 160 }}>
//                     <div className="small fw-semibold mb-1">Amount</div>
//                     <input
//                       type="number"
//                       className="form-control"
//                       value={localEnablePartial ? localPartialAmount : remainingAmount}
//                       onChange={(e) => handleAmount(Number(e.target.value))}
//                       disabled={!localEnablePartial}
//                     />
//                   </div>
//                   <button
//                     type="button"
//                     className="btn btn-primary"
//                     onClick={() => onSubmitOffline?.(selectedPaymentMethod as string)}
//                   >
//                     Submit Payment
//                   </button>
//                 </div>
//               </>
//             )}

//             {/* Online (Stripe/custom) */}
//             {selectedPaymentMethod === "online" && onlinePaymentNode && <div className="mt-3">{onlinePaymentNode}</div>}

//             {/* Dynamic methods (skip if external PaymentMethods node provided) */}
//             {dynamicMethods.length > 0 && !paymentMethodsNode && (
//               <div className="mt-2">
//                 {dynamicMethods.map((m) => (
//                   <div
//                     key={m._id}
//                     className={`method-row ${selectedPaymentMethod === m.name ? "active" : ""}`}
//                     onClick={() => setSelectedPaymentMethod(m.name)}
//                   >
//                     <div className="form-check m-0">
//                       <input
//                         className="form-check-input"
//                         type="radio"
//                         name="paymentMethod"
//                         id={`pm_dyn_${m._id}`}
//                         checked={selectedPaymentMethod === m.name}
//                         onChange={() => setSelectedPaymentMethod(m.name)}
//                       />
//                       <label htmlFor={`pm_dyn_${m._id}`} className="form-check-label">
//                         {m.name}
//                       </label>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}

//             {/* External PaymentMethods node (optional, injected from parent) */}
//             {paymentMethodsNode ? <div className="mt-3">{paymentMethodsNode}</div> : null}

//             {/* Remaining balance (only when partial enabled) */}
//             {enablePartial && localEnablePartial && (
//               <div className="mt-3 text-muted small">
//                 Remaining Balance: $
//                 {(Number(remainingAmount) - Number(localPartialAmount || 0)).toFixed(2)}
//               </div>
//             )}
//           </>
//         )}
//       </div>

//       {/* styles for the pill rows + exact look */}
//       <style>{`
//         .rounded-4 { border-radius: 16px; }
//         .card-header { border-bottom: 1px solid #edf1f5; }
//         .icon-chip {
//           width: 28px; height: 28px; border-radius: 8px;
//           display:inline-flex; align-items:center; justify-content:center;
//           font-size:16px;
//         }
//         .chip-green { background:#EAF7F1; color:#10B981; }
//         .method-row {
//           border: 1px solid #e7e9ef;
//           border-radius: 12px;
//           padding: 12px 14px;
//           margin-bottom: 12px;
//           cursor: pointer;
//           transition: box-shadow .15s ease, border-color .15s ease;
//           background: #fff;
//         }
//         .method-row.active {
//           border-color: #0d6efd;
//           box-shadow: 0 0 0 3px rgba(13,110,253,.12);
//         }
//         .soft-input {
//           background: #f3f6fb;
//           border-color: #e9eef5;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default PaymentPanel;


































// // components/payments/PaymentPanel.tsx
// import React from "react";

// type Variant = "signage" | "realtor" | "agent" | "teamMember" | "other";
// type DynMethod = { _id: string; name: string };

// /** Minimal surface from Formik we actually use (structurally compatible). */
// type FormikLike = {
//   values: {
//     enable_partial_payment?: boolean;
//     partial_amount?: number;
//     transactionId?: string;
//     [k: string]: any;
//   };
//   setFieldValue: (field: string, value: any, shouldValidate?: boolean) => any;
//   handleChange: (e: React.ChangeEvent<any>) => void;
//   handleBlur: (e: React.FocusEvent<any>) => void;
// };

// type Props = {
//   variant: Variant;                          // "signage" => all methods + partial
//   remainingAmount: number;
//   orderStatus: number;
//   paymentStatus: string;

//   selectedPaymentMethod: string | null;
//   setSelectedPaymentMethod: (v: string) => void;

//   /** Optional (but recommended for signage/partial) */
//   formik?: FormikLike;
//   maxAllowed: number;

//   dynamicMethods?: DynMethod[];

//   onSubmitOffline: (methodKey: string) => void; // cheque/bank_transfer/dynamic

//   /** Your Stripe node (e.g., <Elements>...</Elements>) */
//   onlinePaymentNode?: React.ReactNode;
//   paymentMethodsNode?: React.ReactNode;
// };

// const PaymentPanel: React.FC<Props> = ({
//   variant,
//   remainingAmount,
//   orderStatus,
//   paymentStatus,
//   selectedPaymentMethod,
//   setSelectedPaymentMethod,
//   formik,
//   maxAllowed,
//   dynamicMethods = [],
//   onSubmitOffline,
//   onlinePaymentNode,
//   paymentMethodsNode
// }) => {
//   if (!orderStatus || orderStatus <= 100) return null;

//   const isPaid = paymentStatus === "Paid" || Number(remainingAmount) === 0;
//   const isSignage = variant === "signage";

//   // visible static methods
//   const visibleStatic: Array<"cheque" | "bank_transfer" | "online"> = isSignage
//     ? ["cheque", "bank_transfer", "online"]
//     : ["online"];

//   const labelFor = (k: string) =>
//     ({ cheque: "Cheque", bank_transfer: "Bank Transfer", online: "Credit/Debit Card" } as any)[k] || k;

//   // ---- Local fallbacks so component works even if formik is not passed ----
//   const [localEnablePartial, setLocalEnablePartial] = React.useState<boolean>(
//     Boolean(formik?.values?.enable_partial_payment)
//   );
//   const [localPartialAmount, setLocalPartialAmount] = React.useState<number>(
//     Number(formik?.values?.partial_amount ?? 0)
//   );
//   const [localTxnId, setLocalTxnId] = React.useState<string>(String(formik?.values?.transactionId ?? ""));

//   // keep locals in sync if parent Formik updates
//   React.useEffect(() => {
//     if (!formik) return;
//     setLocalEnablePartial(Boolean(formik.values?.enable_partial_payment));
//     setLocalPartialAmount(Number(formik.values?.partial_amount ?? 0));
//     setLocalTxnId(String(formik.values?.transactionId ?? ""));
//   }, [formik?.values?.enable_partial_payment, formik?.values?.partial_amount, formik?.values?.transactionId]);

//   const enablePartial = isSignage && !isPaid;

//   const togglePartial = () => {
//     const next = !localEnablePartial;
//     setLocalEnablePartial(next);
//     formik?.setFieldValue?.("enable_partial_payment", next);
//     if (!next) {
//       setLocalPartialAmount(0);
//       formik?.setFieldValue?.("partial_amount", 0);
//     }
//   };

//   const handleAmount = (v: number) => {
//     const value = Number(isFinite(v as any) ? v : 0);
//     const clamped = value <= maxAllowed ? value : maxAllowed;
//     setLocalPartialAmount(clamped);
//     formik?.setFieldValue?.("partial_amount", clamped);
//   };

//   const handleTxn = (val: string) => {
//     setLocalTxnId(val);
//     formik?.setFieldValue?.("transactionId", val);
//   };

//   const showOfflineExtras = selectedPaymentMethod && selectedPaymentMethod !== "online";

//   return (
//     <div className="card shadow-sm rounded-4 border-0 mt-4">
//       <div className="card-header bg-white rounded-top border-0 py-3 d-flex justify-content-between align-items-center">
//         <h6 className="mb-0 d-flex align-items-center">
//           <span className="icon-chip chip-green me-2">
//             <i className="bi bi-cash-coin" />
//           </span>
//           Payment
//         </h6>
//         {!isPaid && <div className="text-danger fw-bold fs-6">${Number(remainingAmount).toFixed(2)}</div>}
//       </div>

//       <div className="card-body">
//         {isPaid ? (
//           <p className="text-success fw-semibold text-center mb-0">Your payment is completed / approved</p>
//         ) : (
//           <>
//             <div className="text-muted small mb-2">Select Payment Method</div>

//             {/* Enable Partial Payment (signage only) */}
//             {enablePartial && (
//               <div className={`method-row ${localEnablePartial ? "active" : ""}`} onClick={togglePartial}>
//                 <div className="form-check m-0">
//                   <input
//                     type="checkbox"
//                     className="form-check-input"
//                     id="enable_partial_payment"
//                     checked={localEnablePartial}
//                     onChange={togglePartial}
//                   />
//                   <label htmlFor="enable_partial_payment" className="form-check-label">
//                     Enable Partial Payment
//                   </label>
//                 </div>
//               </div>
//             )}

//             {/* Static methods */}
//             {visibleStatic.map((key) => (
//               <div
//                 key={key}
//                 className={`method-row ${selectedPaymentMethod === key ? "active" : ""}`}
//                 onClick={() => setSelectedPaymentMethod(key)}
//               >
//                 <div className="form-check m-0">
//                   <input
//                     className="form-check-input"
//                     type="radio"
//                     name="paymentMethod"
//                     id={`pm_${key}`}
//                     checked={selectedPaymentMethod === key}
//                     onChange={() => setSelectedPaymentMethod(key)}
//                   />
//                   <label htmlFor={`pm_${key}`} className="form-check-label">
//                     {labelFor(key)}
//                   </label>
//                 </div>
//               </div>
//             ))}

//             {/* Extra fields for offline static/dynamic selections */}
//             {showOfflineExtras && (
//               <>
//                 <div className="mt-3">
//                   <div className="small fw-semibold mb-1">Transaction ID</div>
//                   <input
//                     type="text"
//                     className="form-control soft-input"
//                     placeholder="Enter transaction ID"
//                     value={localTxnId}
//                     onChange={(e) => handleTxn(e.target.value)}
//                   />
//                 </div>

//                 <div className="mt-3 d-flex align-items-end gap-2">
//                   <div style={{ width: 160 }}>
//                     <div className="small fw-semibold mb-1">Amount</div>
//                     <input
//                       type="number"
//                       className="form-control"
//                       value={localEnablePartial ? localPartialAmount : remainingAmount}
//                       onChange={(e) => handleAmount(Number(e.target.value))}
//                       disabled={!localEnablePartial}
//                     />
//                   </div>
//                   <button
//                     type="button"
//                     className="btn btn-primary"
//                     onClick={() => onSubmitOffline(selectedPaymentMethod as string)}
//                   >
//                     Submit Payment
//                   </button>
//                 </div>
//               </>
//             )}

//             {/* Online (Stripe/custom) */}
//             {selectedPaymentMethod === "online" && onlinePaymentNode && <div className="mt-3">{onlinePaymentNode}</div>}

//             {/* Dynamic methods */}
//             {dynamicMethods.length > 0 && (
//               <div className="mt-2">
//                 {dynamicMethods.map((m) => (
//                   <div
//                     key={m._id}
//                     className={`method-row ${selectedPaymentMethod === m.name ? "active" : ""}`}
//                     onClick={() => setSelectedPaymentMethod(m.name)}
//                   >
//                     <div className="form-check m-0">
//                       <input
//                         className="form-check-input"
//                         type="radio"
//                         name="paymentMethod"
//                         id={`pm_dyn_${m._id}`}
//                         checked={selectedPaymentMethod === m.name}
//                         onChange={() => setSelectedPaymentMethod(m.name)}
//                       />
//                       <label htmlFor={`pm_dyn_${m._id}`} className="form-check-label">
//                         {m.name}
//                       </label>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}

//             {/* Remaining balance (only when partial enabled) */}
//             {enablePartial && localEnablePartial && (
//               <div className="mt-3 text-muted small">
//                 Remaining Balance: $
//                 {(Number(remainingAmount) - Number(localPartialAmount || 0)).toFixed(2)}
//               </div>
//             )}
//           </>
//         )}
//       </div>

//       {/* styles for the pill rows + exact look */}
//       <style>{`
//         .rounded-4 { border-radius: 16px; }
//         .card-header { border-bottom: 1px solid #edf1f5; }
//         .icon-chip {
//           width: 28px; height: 28px; border-radius: 8px;
//           display:inline-flex; align-items:center; justify-content:center;
//           font-size:16px;
//         }
//         .chip-green { background:#EAF7F1; color:#10B981; }
//         .method-row {
//           border: 1px solid #e7e9ef;
//           border-radius: 12px;
//           padding: 12px 14px;
//           margin-bottom: 12px;
//           cursor: pointer;
//           transition: box-shadow .15s ease, border-color .15s ease;
//           background: #fff;
//         }
//         .method-row.active {
//           border-color: #0d6efd;
//           box-shadow: 0 0 0 3px rgba(13,110,253,.12);
//         }
//         .soft-input {
//           background: #f3f6fb;
//           border-color: #e9eef5;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default PaymentPanel;
